import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  capabilities,
  logoUrl,
  platformModules,
  processSteps,
  trustedStats,
  whoWeWorkWithCards,
} from "@/lib/site-data";

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55, ease: "easeOut" },
};

export const HomePage = () => {
  return (
    <div className="mx-auto max-w-7xl space-y-20 px-6 pb-20 pt-14 md:px-10 md:pt-20">
      <section className="grid items-center gap-10 lg:grid-cols-2" data-testid="home-hero-section">
        <motion.div {...fadeIn}>
          <p className="mb-4 text-sm uppercase tracking-[0.2em] text-[#6FE3C1]" data-testid="home-hero-kicker">
            The AI Matters · Hiring Intelligence Platform
          </p>
          <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl" data-testid="home-hero-heading">
            AI Hiring Infrastructure for Modern Companies
          </h1>
          <p className="mt-6 max-w-2xl text-base text-[#D9E1E8]/80 md:text-lg" data-testid="home-hero-subtext">
            Source, evaluate, and hire better candidates using AI-powered candidate scoring, a powerful recruiter
            network, and structured hiring workflows.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button asChild className="bg-[#2EC4A6] text-[#071A2C] hover:bg-[#6FE3C1]" data-testid="home-hero-book-demo-button">
              <Link to="/contact">Book a Demo</Link>
            </Button>
            <Button asChild variant="outline" className="border-[#2EC4A6] bg-transparent text-[#6FE3C1] hover:bg-[#2EC4A6]/15" data-testid="home-hero-explore-button">
              <Link to="/platform" className="flex items-center gap-2">
                Explore JobScore Matters <ArrowRight size={16} />
              </Link>
            </Button>
          </div>
        </motion.div>

        <motion.div
          {...fadeIn}
          className="glass-panel relative overflow-hidden rounded-3xl p-8 md:p-10"
          data-testid="home-hero-visual-panel"
        >
          <div className="hero-visual-pulse pointer-events-none absolute -left-12 -top-12 h-40 w-40 rounded-full" />
          <div className="hero-visual-pulse pointer-events-none absolute -bottom-12 -right-12 h-52 w-52 rounded-full" />
          <img
            src={logoUrl}
            alt="The AI Matters circular logo"
            className="mx-auto aspect-square w-full max-w-xs rounded-full glow-ring"
            data-testid="home-hero-logo-image"
          />
          <div className="mt-6 rounded-2xl border border-white/10 bg-[#071A2C]/70 p-4" data-testid="home-hero-ai-visual-card">
            <p className="text-xs uppercase tracking-[0.16em] text-[#6FE3C1]" data-testid="home-hero-ai-visual-label">
              Live Hiring Intelligence
            </p>
            <p className="mt-2 text-sm text-[#D9E1E8]/85" data-testid="home-hero-ai-visual-text">
              Candidate score confidence, network response speed, and workflow completion synced in real time.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="space-y-8" data-testid="home-capabilities-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-capabilities-heading">Core Capabilities</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {capabilities.map((item, index) => (
            <motion.div key={item.title} {...fadeIn} transition={{ ...fadeIn.transition, delay: index * 0.07 }}>
              <Card className="premium-card h-full border-white/10 bg-[#0B253C]/60">
                <CardHeader>
                  <CardTitle className="text-xl text-[#6FE3C1]" data-testid={`capability-${index + 1}-title`}>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#D9E1E8]/80" data-testid={`capability-${index + 1}-description`}>{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-8 md:p-12" data-testid="home-platform-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-platform-heading">JobScore Matters Platform</h2>
        <p className="mt-4 max-w-3xl text-[#D9E1E8]/80" data-testid="home-platform-subtext">
          Product modules built for measurable hiring execution.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {platformModules.map((module, index) => (
            <Card key={module.title} className="premium-card border-white/10 bg-[#071A2C]/70">
              <CardHeader>
                <CardTitle className="text-xl text-[#6FE3C1]" data-testid={`platform-module-${index + 1}-title`}>
                  {module.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#D9E1E8]/80" data-testid={`platform-module-${index + 1}-description`}>
                  {module.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-7" data-testid="home-trusted-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-trusted-heading">Trusted by Growing Companies</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {trustedStats.map((stat, index) => (
            <Card key={stat} className="premium-card border-[#2EC4A6]/20 bg-[#0B253C]/60 p-6 text-center">
              <p className="text-2xl font-semibold text-[#6FE3C1]" data-testid={`trusted-stat-${index + 1}`}>
                {stat}
              </p>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-8" data-testid="home-how-it-works-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-how-it-works-heading">How It Works</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {processSteps.map((step, index) => (
            <Card key={step} className="premium-card border-[#2EC4A6]/20 bg-[#0B253C]/50 p-5">
              <p className="text-xs uppercase tracking-widest text-[#6FE3C1]" data-testid={`workflow-step-${index + 1}-label`}>Step {index + 1}</p>
              <p className="mt-3 text-[#D9E1E8]" data-testid={`workflow-step-${index + 1}-text`}>{step}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-6" data-testid="home-who-we-work-with-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-who-we-work-with-heading">Who We Work With</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {whoWeWorkWithCards.map((item, index) => (
            <Card key={item.title} className="premium-card border-white/10 bg-[#0B253C]/60">
              <CardHeader>
                <CardTitle data-testid={`who-we-work-with-card-${index + 1}-title`}>{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#D9E1E8]/80" data-testid={`who-we-work-with-card-${index + 1}-description`}>
                  {item.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-8 md:p-10" data-testid="home-recruiter-network-section">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#6FE3C1]" data-testid="home-recruiter-kicker">
              Partner Ecosystem
            </p>
            <h2 className="mt-3 text-3xl font-semibold md:text-4xl" data-testid="home-recruiter-heading">
              Join the Recruiter Network
            </h2>
            <p className="mt-4 max-w-3xl text-[#D9E1E8]/80" data-testid="home-recruiter-description">
              Independent recruiters and vendors can submit candidates through the platform and earn commissions when
              candidates are hired.
            </p>
          </div>
          <Button asChild className="bg-[#2EC4A6] text-[#071A2C] hover:bg-[#6FE3C1]" data-testid="home-join-recruiter-button">
            <Link to="/recruiter-network" className="flex items-center gap-2">
              Join as a Recruiter <Sparkles size={16} />
            </Link>
          </Button>
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-8 text-center md:p-12" data-testid="home-final-cta-section">
        <h2 className="text-3xl font-semibold md:text-4xl" data-testid="home-final-cta-heading">Ready to accelerate hiring with AI?</h2>
        <p className="mx-auto mt-4 max-w-2xl text-[#D9E1E8]/80" data-testid="home-final-cta-subtext">
          Book a demo to see how The AI Matters helps your team hire better talent in less time.
        </p>
        <Button asChild className="mt-8 bg-[#2EC4A6] text-[#071A2C] hover:bg-[#6FE3C1]" data-testid="home-final-book-demo-button">
          <Link to="/contact">Book a Demo</Link>
        </Button>
      </section>
    </div>
  );
};
